import type { IEditTarget, EditResult, SuccessfulEdit } from "../global";
import state from "./state";


/** Navigates to and selects the specified edit target in the Word document. */
export async function edit(
  edit: EditResult
): Promise<SuccessfulEdit | null> {
  if (!state.loaded) {
    console.error("Could not load jumpTo, state is not loaded yet");
    return null;
  }

  if (!state.enabled) {
    console.error("Could not load jumpTo, state is not loaded yet");
    return null;
  }

  return Word.run(async (context) => {
    const result = await findRange(context, edit);
    console.log(`Jump to executed in ${result.ms}ms`);
    await context.sync();
    result.range.select("Select");
    return result;
  }).catch((err) => {
    console.error(err);
    return null;
  });
}

/**
 * Finds and returns the range for an edit target.
 * Returns a paragraph outcome when no segment is specified, a segment outcome when the segment is found,
 * or a paragraph-fallback outcome when segment resolution fails after the paragraph is found.
 */
export async function findRange(
  context: Word.RequestContext,
  edit: EditResult
): Promise<SuccessfulEdit> {
  const start = Date.now();
  const result = await findParagraph(context, edit.paragraph);
  const paragraphRange = result.range;

  if (!edit.segment) {
    return {
      target: edit,
      ms: Date.now() - start,
      range: paragraphRange,
      outcome: "paragraph",
    };
  }

  try {
    const segmentRange = await findSegmentRange(
      context,
      paragraphRange,
      edit.segment
    );
    return {
      target: edit,
      ms: Date.now() - start,
      range: segmentRange,
      outcome: "segment",
    };
  } catch (error: unknown) {
    console.error("failed to match the segment, falling back to paragraph");
    return {
      target: edit,
      ms: Date.now() - start,
      range: paragraphRange,
      outcome: "paragraph-fallback",
      failure: { stage: "segment", error },
    };
  }
}

/** Finds and returns the paragraph range matching the given edit target. */
export async function findParagraph(
  context: Word.RequestContext,
  target: IEditTarget
) {
  const start = Date.now();
  const range =
    target.query.length == 0
      ? await getParagraphFromAdjustment(context, target)
      : await getParagraphFromSearch(context, target);

  const ms = Date.now() - start;
  return { target, range, ms };
}

async function getParagraphFromSearch(
  context: Word.RequestContext,
  target: IEditTarget
) {
  const ps = await searchParagraphs(context, target.query, target.index);
  const paragraph = ps[0];
  if (!paragraph) {
    throw createTargetNotFoundError("paragraph", target);
  }

  return applyOffset(paragraph, target.offset);
}

async function getParagraphFromAdjustment(
  context: Word.RequestContext,
  target: IEditTarget
) {
  const ps = context.document.body.paragraphs;
  context.load(ps, "items");
  await context.sync();

  if (target.offset > 0) {
    return ps.items[target.offset - 1];
  }
  if (target.offset < 0) {
    return ps.items[ps.items.length + target.offset];
  }

  throw "invalid offset";
}

/** Search options for paragraph text matching in Word documents. */
export const searchParagraphOptions = {
  matchCase: false,
  matchWholeWord: false,
  matchWildcards: false,
  ignoreSpace: false,
};

/** Searches the document for paragraphs containing the specified query text. */
export async function searchParagraphs(
  context: Word.RequestContext,
  query: string,
  index?: number
): Promise<Word.Paragraph[]> {
  const searchResults = context.document.body.search(
    query,
    searchParagraphOptions
  );

  const options =
    index != undefined ? { skip: index, top: 1, select: "items" } : "items";
  context.load(searchResults, options);
  await context.sync();

  for (let item of searchResults.items) {
    context.load(item.paragraphs, { skip: 0, top: 1, select: "items" });
  }
  await context.sync();

  const results = [];
  for (let item of searchResults.items) {
    results.push(item.paragraphs.items[0]);
  }
  return results;
}

/** Applies a relative offset to navigate forward or backward through paragraphs. */
export function applyOffset(
  paragraph: Word.Paragraph,
  offset: number
): Word.Paragraph {
  while (offset < 0) {
    paragraph = paragraph.getPrevious();
    offset++;
  }

  while (offset > 0) {
    paragraph = paragraph.getNext();
    offset--;
  }

  return paragraph;
}

/** Finds a specific text segment within a paragraph based on the target query. Throws when the segment target is not found. */
export async function findSegmentRange(
  context: Word.RequestContext,
  paragraph: Word.Paragraph,
  target: IEditTarget
): Promise<Word.Range> {
  const results = paragraph.search(target.query);
  context.load(results, { top: 1, skip: target.index, select: "items" });
  await context.sync();

  const range = results.items[0];
  if (!range) {
    throw createTargetNotFoundError("segment", target);
  }

  return range;
}

function createTargetNotFoundError(
  kind: "paragraph" | "segment",
  target: IEditTarget
): Error {
  return new Error(
    `Could not find ${kind} target: query=${JSON.stringify(target.query)}, index=${target.index}, offset=${target.offset}`
  );
}
